import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Bell, MessageSquare, Mail } from 'lucide-react';
import { customersAPI } from '../lib/api';
import { toast } from 'sonner';

const CONSENT_OPTIONS = [
  { value: 'opted_in', label: 'Opted In', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400' },
  { value: 'opted_out', label: 'Opted Out', cls: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400' },
  { value: 'unknown', label: 'Unknown', cls: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400' },
];

function ConsentChip({ value, onChange }) {
  const current = CONSENT_OPTIONS.find(o => o.value === value) || CONSENT_OPTIONS[2];
  const nextIdx = (CONSENT_OPTIONS.findIndex(o => o.value === value) + 1) % CONSENT_OPTIONS.length;

  return (
    <Badge
      variant="outline"
      className={`cursor-pointer select-none text-xs px-2.5 py-0.5 transition-colors ${current.cls}`}
      onClick={() => onChange(CONSENT_OPTIONS[nextIdx].value)}
      data-testid={`consent-chip-${value}`}
    >
      {current.label}
    </Badge>
  );
}

export default function CommunicationPreferencesCard({ customerId, customer, onUpdate }) {
  const [saving, setSaving] = useState(false);

  const handleToggle = async (field, value) => {
    setSaving(true);
    try {
      const res = await customersAPI.update(customerId, { [field]: value });
      onUpdate(res.data);
    } catch {
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="communication-preferences-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Bell className="w-4 h-4" /> Communication Preferences
        </CardTitle>
      </CardHeader>
      <CardContent className={`space-y-4 ${saving ? 'opacity-60 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-2 text-sm">
            <Bell className="w-3.5 h-3.5 text-muted-foreground" />
            <span>Notifications Enabled</span>
          </div>
          <Switch
            checked={customer.notifications_enabled !== false}
            onCheckedChange={(v) => handleToggle('notifications_enabled', v)}
            data-testid="toggle-notifications"
          />
        </div>
        <div className="border-t" />
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-2 text-sm">
            <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
            <span>SMS Consent</span>
          </div>
          <ConsentChip
            value={customer.sms_consent || 'unknown'}
            onChange={(v) => handleToggle('sms_consent', v)}
          />
        </div>
        <div className="border-t" />
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-2 text-sm">
            <Mail className="w-3.5 h-3.5 text-muted-foreground" />
            <span>Email Marketing Consent</span>
          </div>
          <ConsentChip
            value={customer.email_marketing_consent || 'unknown'}
            onChange={(v) => handleToggle('email_marketing_consent', v)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
